import React, { Fragment, useCallback, useEffect, useState } from 'react'
import UserLayout from '../../layouts/UserLayout'
import {
	Backdrop,
	Box,
	Button,
	Card,
	CircularProgress,
	Divider,
	IconButton,
	Typography,
	useMediaQuery,
} from '@mui/material'
import { getFromStorage } from '../../utils/storage'
import { useDispatch, useSelector } from 'react-redux'
import { handleError } from '../../utils/error'
import { currentUserActions } from '../../redux/store'
import { DOMAIN, api } from '../../utils/api'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined'
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded'
import { mainBlue, mainGreen, mainRed } from '../../utils/color'
import { apiBase } from '../../services/apiBase'
import { enqueueSnackbar } from 'notistack'

export default function ConfirmStockMultiPic() {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const mediaMd = useMediaQuery('(min-width:768px)')
	const token = getFromStorage('current-user')?.accessToken
	const { currentUser } = useSelector((state) => state.currentUser)
	const [confirmReImportFresh, setConfirmReImportFresh] = useState([])
	const [confirmCheckStock, setConfirmCheckStock] = useState()
	const [confirmCheckFreshStock, setConfirmCheckFreshStock] = useState()
	const [currentImage, setCurrentImage] = useState(null)
	const [isViewerOpen, setIsViewerOpen] = useState(false)
	const [confirmSellExport, setConfirmSellExport] = useState([])
	const [confirmReImportCookedStock, setConfirmReImportCookedStock] = useState([])
	const [fileUpload, setFileUpload] = useState([])
	const [showDeleteOrderBackDrop, setShowDeleteOrderBackDrop] = useState(false)
	const [loadingConfirmDelete, setLoadingConfirmDelete] = useState(false)
	const [disabled, setDisabled] = useState(false)
	const [subStock, setSubStock] = useState({
		freshInventory: '',
		cookedInventory: '',
	})
	const [preview, setPreview] = useState([])
	const [internalOrders, setInternalOrders] = useState([])

	const openImageViewer = (photo) => {
		setCurrentImage(photo)
		setIsViewerOpen(true)
	}

	const closeImageViewer = () => {
		setCurrentImage(null)
		setIsViewerOpen(false)
	}

	const onFileChange = async (e) => {
		setFileUpload(e.target.files)
	}

	useEffect(() => {
		if (!fileUpload) {
			setPreview(undefined)
			return
		}
		let preview = []
		for (let i = 0; i < fileUpload.length; i++) {
			preview.push(URL.createObjectURL(fileUpload[i]))
		}
		setPreview(preview)
	}, [fileUpload])

	const getSubStock = useCallback(
		async function () {
			try {
				const response = await axios({
					url: `${DOMAIN}/sub-stock/${currentUser.store}`,
					method: 'GET',
					headers: {
						Authorization: `Bearer ${token}`,
					},
				})
				setSubStock(response.data[0])
			} catch (err) {
				handleError(err)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			}
		},
		[dispatch, navigate, token, currentUser.store],
	)

	useEffect(() => {
		getSubStock()
	}, [getSubStock])

	const getConfirmReImportFresh = useCallback(
		async function () {
			try {
				const response = await axios({
					url: `${DOMAIN}/sub-stock/confirm-reimport-fresh/${currentUser.store}`,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'GET',
				})
				setConfirmReImportFresh(response.data)
			} catch (error) {
				console.log(error.response.data.message)
			}
		},
		[token, currentUser.store],
	)

	useEffect(() => {
		if (currentUser.permission?.includes('confirmReImportFresh')) {
			getConfirmReImportFresh()
		}
	}, [getConfirmReImportFresh, currentUser.permission])

	const getConfirmSellExportStock = useCallback(
		async function () {
			try {
				const response = await axios({
					url: `${DOMAIN}/sub-stock/confirm-sell-export-stock/${currentUser.store}`,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'GET',
				})
				setConfirmSellExport(response.data)
			} catch (error) {
				console.log(error.response.data.message)
			}
		},
		[token, currentUser.store],
	)

	useEffect(() => {
		if (currentUser.permission?.includes('confirmSellExport')) {
			getConfirmSellExportStock()
		}
	}, [getConfirmSellExportStock, currentUser.permission])

	const getConfirmReImportCookedStock = useCallback(
		async function () {
			try {
				const response = await axios({
					url: `${DOMAIN}/sub-stock/confirm-reimport-cooked-stock/${currentUser.store}`,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'GET',
				})
				setConfirmReImportCookedStock(response.data)
			} catch (error) {
				console.log(error.response.data.message)
			}
		},
		[token, currentUser.store],
	)

	useEffect(() => {
		if (currentUser.permission?.includes('confirmReImportCooked')) {
			getConfirmReImportCookedStock()
		}
	}, [getConfirmReImportCookedStock, currentUser.permission])

	const confirmReImportCookedStockSubmit = async (id, user) => {
		setLoadingConfirmDelete(true)
		try {
			setDisabled(true)
			const formData = new FormData()
			formData.append('id', id)
			formData.append('user', user)
			for (let i = 0; i < fileUpload.length; i++) {
				formData.append('image', fileUpload[i])
			}
			const res = await axios({
				url: `${DOMAIN}/sub-stock/confirm-reimport-cooked-stock/${currentUser.store}`,
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'multipart/form-data',
				},
				method: 'PUT',
				data: formData,
			})
			if (res.data.message) {
				alert(res.data.message)
				setDisabled(false)
			}
			await getConfirmReImportCookedStock()
			await getSubStock()
			setFileUpload([])
			setShowDeleteOrderBackDrop(false)
			setLoadingConfirmDelete(false)
			setDisabled(false)
			await getSubStock()
		} catch (err) {
			handleError(err)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const getConfirmCheckStock = useCallback(
		async function () {
			try {
				const response = await axios({
					url: `${DOMAIN}/sub-stock/confirm-check-product-stock/${currentUser.store}`,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'GET',
				})
				setConfirmCheckStock(response.data)
			} catch (error) {
				console.log(error.response.data.message)
			}
		},
		[token, currentUser.store],
	)

	useEffect(() => {
		if (currentUser.permission?.includes('confirmCheckStock')) {
			getConfirmCheckStock()
		}
	}, [getConfirmCheckStock, currentUser.permission])

	const confirmReImportFreshSubmit = async function (id) {
		try {
			setDisabled(true)
			const res = await axios({
				url: `${DOMAIN}/sub-stock/confirm-reimport-fresh/${currentUser.store}`,
				headers: {
					Authorization: `Bearer ${token}`,
				},
				method: 'PUT',
				data: { id },
			})
			if (res.data.message) {
				alert(res.data.message)
				setDisabled(false)
			}
			await getConfirmReImportFresh()
			await getSubStock()
			setDisabled(false)
		} catch (error) {
			handleError(error)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const confirmCheckProductStockSubmit = async function (cookedInventory, id) {
		try {
			setDisabled(true)
			const res = await axios({
				url: `${DOMAIN}/sub-stock/confirm-check-product-stock/${currentUser.store}`,
				headers: {
					Authorization: `Bearer ${token}`,
				},
				method: 'PUT',
				data: { cookedInventory, id },
			})
			if (res.data.message) {
				setDisabled(false)
				alert(res.data.message)
				return
			}
			await getConfirmCheckStock()
			await getSubStock()
			setDisabled(false)
		} catch (error) {
			handleError(error)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const confirmCheckFreshStockSubmit = async function (freshInventory, id) {
		try {
			setDisabled(true)
			const res = await axios({
				url: `${DOMAIN}/sub-stock/confirm-check-fresh-stock/${currentUser.store}`,
				headers: {
					Authorization: `Bearer ${token}`,
				},
				method: 'PUT',
				data: { freshInventory, id },
			})
			if (res.data.message) {
				setDisabled(false)
				alert(res.data.message)
				return
			}
			await getConfirmCheckFreshStock()
			await getSubStock()
			setDisabled(false)
		} catch (error) {
			handleError(error)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const getConfirmCheckFreshStock = useCallback(
		async function () {
			try {
				const response = await axios({
					url: `${DOMAIN}/sub-stock/confirm-check-fresh-stock/${currentUser.store}`,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'GET',
				})
				setConfirmCheckFreshStock(response.data)
			} catch (error) {
				console.log(error.response.data.message)
			}
		},
		[token, currentUser.store],
	)

	useEffect(() => {
		if (currentUser.permission?.includes('confirmCheckStock')) {
			getConfirmCheckFreshStock()
		}
	}, [getConfirmCheckFreshStock, currentUser.permission])

	const cancelReImportFresh = async (id) => {
		try {
			await axios({
				url: `${DOMAIN}/sub-stock/cancel-reimport-fresh/${currentUser.store}`,
				method: 'PUT',
				headers: {
					Authorization: `Bearer ${token}`,
				},
				data: {
					id,
				},
			})
			await getConfirmReImportFresh()
		} catch (err) {
			handleError(err)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const cancelCheckFreshStock = async () => {
		try {
			await axios({
				url: `${DOMAIN}/sub-stock/cancel-check-fresh-stock/${currentUser.store}`,
				method: 'PUT',
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			await getConfirmCheckFreshStock()
		} catch (err) {
			handleError(err)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const cancelCheckProductStock = async () => {
		try {
			await axios({
				url: `${DOMAIN}/sub-stock/cancel-check-product-stock/${currentUser.store}`,
				method: 'PUT',
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			await getConfirmCheckStock()
		} catch (err) {
			handleError(err)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const undoDeleteOrder = async (orderId) => {
		try {
			await axios({
				url: `${DOMAIN}/sub-stock/undo-delete-order/${currentUser.store}`,
				method: 'PUT',
				headers: {
					Authorization: `Bearer ${token}`,
				},
				data: {
					orderId,
				},
			})
			await getConfirmReImportCookedStock()
		} catch (err) {
			handleError(err)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const undoPendingProduceOrder = async (id) => {
		try {
			await axios({
				url: `${DOMAIN}/sub-stock/undo-pending-produce-order/${currentUser.store}`,
				method: 'PUT',
				headers: {
					Authorization: `Bearer ${token}`,
				},
				data: {
					id,
				},
			})
			await getConfirmSellExportStock()
		} catch (err) {
			handleError(err)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const startProduceOrder = async (data) => {
		try {
			setDisabled(true)
			const res = await axios({
				url: api.POST_START_PRODUCE_ORDER,
				method: 'POST',
				headers: {
					Authorization: `Bearer ${token}`,
				},
				data,
			})
			if (res.data.message) {
				alert(res.data.message)
				setDisabled(false)
			}
			await getSubStock()
			await getConfirmSellExportStock()
			setDisabled(false)
		} catch (err) {
			handleError(err)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const confirmQuickOrder = async (data) => {
		try {
			setDisabled(true)
			const res = await axios({
				url: api.CONFIRM_SUCCESS_QUICK_ORDER,
				method: 'POST',
				headers: {
					Authorization: `Bearer ${token}`,
				},
				data,
			})
			if (res.data.message) {
				alert(res.data.message)
				setDisabled(false)
			}
			await getSubStock()
			await getConfirmSellExportStock()
			setDisabled(false)
		} catch (err) {
			handleError(err)
			dispatch(currentUserActions.isLogout())
			navigate('/login')
		}
	}

	const getInternalOrders = useCallback(async () => {
		try {
			const res = await apiBase.get(api.GET_INTERNAL_SALE_ORDER_REQUESTS)
			if (Array.isArray(res)) {
				setInternalOrders(res)
			}
		} catch (error) {
			enqueueSnackbar(error?.response?.data?.message, { variant: 'error' })
		}
	}, [])

	const actionInternalOrder = useCallback(
		async (id, action) => {
			try {
				await apiBase.post(api.POST_INTERNAL_SALE_ORDER_REQUEST, { orderId: id, checkerAction: action })
				await getInternalOrders()
				enqueueSnackbar('Đã xử lý đơn nội bộ', { variant: 'success' })
			} catch (error) {
				enqueueSnackbar(error?.response?.data?.message, { variant: 'error' })
			}
		},
		[getInternalOrders],
	)

	useEffect(() => {
		getInternalOrders()
	}, [getInternalOrders])

	const refresh = function () {
		getSubStock()
		getConfirmSellExportStock()
		getConfirmReImportCookedStock()
		getConfirmReImportFresh()
		getConfirmCheckStock()
		getConfirmCheckFreshStock()
		getInternalOrders()
	}
	return (
		<UserLayout>
			<Box
				sx={{
					maxWidth: 992,
					margin: '0 auto',
					pl: 1,
					pr: 1,
				}}>
				<Box display='flex' justifyContent='space-between' alignItems='flex-end' sx={{ mt: 1, mb: 1 }}>
					<Button
						sx={{
							textTransform: 'none',
							padding: '3px',
							color: mainGreen,
							borderColor: mainGreen,
							'&:hover': {
								borderColor: mainGreen,
							},
						}}
						variant='outlined'
						size='small'
						onClick={refresh}>
						<AutorenewOutlinedIcon sx={{ fontSize: '16px' }} />
						Cập nhật
					</Button>
					{currentUser.permission?.includes('viewStockHistory') && (
						<Button
							sx={{
								padding: '3px 10px',
								textTransform: 'none',
								background: mainRed,
								'&:hover': { background: mainRed },
							}}
							size='small'
							variant='contained'
							onClick={() => {
								navigate('/store-manager/stock/view-history')
							}}>
							Lịch sử kho
						</Button>
					)}
				</Box>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						color: '#fff',
						background: mainGreen,
						padding: '4px 8px',
						borderRadius: '4px',
					}}>
					<Typography
						sx={{
							textAlign: 'center',
							fontWeight: 'bold',
							fontSize: '14px',
						}}>
						Tồn sống:{' '}
						{String(subStock?.freshInventory) !== ''
							? Number(subStock?.freshInventory)?.toFixed(1)
							: subStock?.freshInventory}
						kg
					</Typography>
					<Typography
						sx={{
							textAlign: 'center',
							fontWeight: 'bold',
							fontSize: '14px',
						}}>
						Tồn chín:{' '}
						{String(subStock?.cookedInventory) !== ''
							? Number(subStock?.cookedInventory)?.toFixed(1)
							: subStock?.cookedInventory}
						kg
					</Typography>
				</Box>
				{currentUser.permission?.includes('confirmReImportFresh') && (
					<Card
						sx={{
							mb: 1,
							padding: '10px',
						}}>
						<Typography
							sx={{
								textAlign: 'center',
								fontSize: '18px',
								fontWeight: 'bold',
								mb: 1,
							}}>
							TÁI NHẬP SỐNG
						</Typography>
						{confirmReImportFresh?.length === 0 && (
							<Typography sx={{ textAlign: 'center', fontStyle: 'italic' }}>(Chưa có dữ liệu)</Typography>
						)}
						{confirmReImportFresh?.map((item, index) => {
							return (
								<Fragment key={index}>
									<Box>
										<Typography sx={{ textAlign: 'left', mt: 1 }}>
											<b>{item.user.name}</b>{' '}
											{item?.action === 'confirmReImportFresh' && (
												<span>
													đề xuất tái nhập <b>{item?.quantity?.toFixed(1)}kg</b>
												</span>
											)}{' '}
											{item?.reImportFreshReason && (
												<span
													style={{
														display: 'block',
														fontStyle: 'italic',
														margin: 0,
													}}>
													<span style={{ fontSize: '12px', fontWeight: 'bold' }}>{item?.timeStamp}</span> - Lý do:{' '}
													{item?.reImportFreshReason}
												</span>
											)}
										</Typography>
									</Box>
									{item?.reImportFreshPhoto?.map((img, index) => {
										return (
											<img
												loading='lazy'
												key={index}
												src={img}
												onClick={(e) => {
													openImageViewer(img)
												}}
												width='30'
												height='30'
												style={{
													marginRight: '8px',
													marginTop: '2px',
													cursor: 'pointer',
												}}
												alt=''
											/>
										)
									})}
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											mt: 1,
										}}>
										<Button
											disabled={disabled}
											onClick={async () => {
												await confirmReImportFreshSubmit(item._id)
											}}
											sx={{
												textTransform: 'none',
												fontSize: '10px',
												minWidth: 0,
												lineHeight: '0.7rem',
												background: mainGreen,
												'&:hover': { background: mainGreen },
											}}
											variant='contained'
											size='small'>
											Duyệt
										</Button>
										<Button
											onClick={() => {
												cancelReImportFresh(item._id)
											}}
											sx={{
												textTransform: 'none',
												fontSize: '10px',
												minWidth: 0,
												lineHeight: '0.7rem',
												background: mainRed,
												'&:hover': { background: mainRed },
											}}
											variant='contained'
											size='small'>
											Hủy
										</Button>
									</Box>
									<Divider sx={{ mt: 1, mb: 1 }} />
								</Fragment>
							)
						})}
					</Card>
				)}
				{currentUser.permission?.includes('confirmSellExport') && (
					<Card sx={{ padding: '10px', mb: 1 }}>
						<Typography
							sx={{
								textAlign: 'center',
								fontSize: '18px',
								fontWeight: 'bold',
								mb: 1,
							}}>
							XUẤT KHO
						</Typography>
						{confirmSellExport?.length === 0 && (
							<Typography sx={{ textAlign: 'center', fontStyle: 'italic' }}>(Chưa có dữ liệu)</Typography>
						)}
						{confirmSellExport?.length > 0 && (
							<Box sx={{ mt: 2 }}>
								{confirmSellExport?.map((item, index) => {
									return (
										<Fragment key={index}>
											<Box>
												<Typography
													sx={{
														color: mainBlue,
														fontWeight: 'bold',
													}}>
													{item.shiftCode}
													{item.shiftIndex}. {item.creator.user.name} xuất bán
													{item.quickPurchase && (
														<>
															{' '}
															<span style={{ color: mainRed }}>nhanh</span>
														</>
													)}
													: {item.creator.createdAt}
												</Typography>
												<span
													style={{
														display: 'inline-block',
														width: '5px',
														height: '5px',
														background: mainGreen,
														borderRadius: '50%',
														marginRight: '5px',
														marginBottom: '2px',
													}}></span>
												<span style={{ fontWeight: 700, color: mainGreen }}>
													{item.quantity}kg - {item.totalPrice.toLocaleString()}đ
												</span>
												<br></br>
												<span> {item.detail}</span>
											</Box>
											<Box
												sx={{
													display: 'flex',
													justifyContent: 'space-between',
													mt: 1,
												}}>
												<Button
													disabled={disabled}
													onClick={async (e) => {
														if (item.quickPurchase) {
															await confirmQuickOrder({
																orderId: item._id,
																store: item.store,
																userId: currentUser?.id,
																accessToken: token,
															})
														} else {
															await startProduceOrder({
																producerId: item.producer.user,
																orderId: item._id,
																store: item.store,
																userId: currentUser?.id,
																accessToken: token,
															})
														}
													}}
													sx={{
														textTransform: 'none',
														fontSize: '10px',
														minWidth: 0,
														lineHeight: '0.7rem',
														background: mainGreen,
														'&:hover': { background: mainGreen },
													}}
													variant='contained'
													size='small'>
													Xuất kho
												</Button>
												{!item.quickPurchase && (
													<Button
														disabled={disabled}
														sx={{
															textTransform: 'none',
															fontSize: '10px',
															minWidth: 0,
															lineHeight: '0.7rem',
															background: mainRed,
															'&:hover': { background: mainRed },
														}}
														variant='contained'
														size='small'
														onClick={() => {
															undoPendingProduceOrder(item._id)
														}}>
														Hoàn tác
													</Button>
												)}
											</Box>
											<Divider sx={{ mt: 1, mb: 1 }} />
										</Fragment>
									)
								})}
							</Box>
						)}
					</Card>
				)}

				{currentUser.permission?.includes('confirmReImportCooked') && (
					<Card sx={{ padding: '10px', mb: 1 }}>
						<Typography
							sx={{
								textAlign: 'center',
								fontSize: '18px',
								fontWeight: 'bold',
								mb: 1,
							}}>
							HỦY ĐƠN
						</Typography>
						{confirmReImportCookedStock?.length === 0 && (
							<Typography sx={{ textAlign: 'center', fontStyle: 'italic' }}>(Chưa có dữ liệu)</Typography>
						)}
						{confirmReImportCookedStock?.length > 0 && (
							<Box sx={{ mt: 2 }}>
								{confirmReImportCookedStock?.map((item, index) => {
									return (
										<Fragment key={index}>
											<Typography
												sx={{
													color: mainRed,
													fontWeight: 'bold',
												}}>
												{item.shiftCode}
												{item.shiftIndex}. {item.deletor.name} hủy đơn
												{item.quickPurchase && (
													<>
														{' '}
														<span style={{ color: mainRed }}>nhanh</span>
													</>
												)}
												: {item.creator.createdAt}
											</Typography>
											<span
												style={{
													display: 'inline-block',
													width: '5px',
													height: '5px',
													background: mainGreen,
													borderRadius: '50%',
													marginRight: '5px',
													marginBottom: '2px',
												}}></span>
											<span style={{ fontWeight: 700, color: mainGreen }}>
												{item.quantity}kg - {item.totalPrice.toLocaleString()}đ
											</span>
											<br></br>
											<span>{item.detail}</span>
											<br></br>
											<span style={{ fontWeight: 'bold' }}>Lý do: {item?.deleteReason}</span>
											<Box
												sx={{
													display: 'flex',
													justifyContent: 'space-between',
													mt: 1,
												}}>
												<Button
													onClick={() => {
														if (confirmCheckStock) {
															alert('Đang chờ xác nhận kiểm kho chín')
															return
														}
														setShowDeleteOrderBackDrop(true)
													}}
													sx={{
														textTransform: 'none',
														fontSize: '10px',
														minWidth: 0,
														lineHeight: '0.7rem',
														background: mainGreen,
														'&:hover': { background: mainGreen },
													}}
													variant='contained'
													size='small'>
													Hủy đơn
												</Button>
												<Backdrop
													sx={{
														color: '#fff',
														zIndex: (theme) => theme.zIndex.drawer + 1,
														left: mediaMd ? 300 : 0,
													}}
													open={showDeleteOrderBackDrop}>
													<Card
														sx={{ padding: '10px 20px', width: '280px' }}
														onClick={(e) => {
															e.stopPropagation()
														}}>
														<Typography
															sx={{
																textAlign: 'center',
																fontSize: '18px',
																fontWeight: 'bold',
																mb: 1,
															}}>
															Ảnh xác nhận hủy đơn
														</Typography>
														{loadingConfirmDelete && (
															<Box sx={{ textAlign: 'center', mt: 2 }}>
																<CircularProgress sx={{ color: mainGreen }} />
															</Box>
														)}
														{!loadingConfirmDelete && (
															<>
																<Box sx={{ mt: 1 }}>
																	{preview?.map((img, index) => {
																		return (
																			<img
																				loading='lazy'
																				key={index}
																				style={{
																					width: 50,
																					marginRight: '8px',
																				}}
																				src={img}
																				alt='preview'
																			/>
																		)
																	})}
																	<Box>
																		<IconButton sx={{ padding: '0' }}>
																			<label
																				htmlFor='choose-photo-delete'
																				style={{
																					display: 'flex',
																					cursor: 'pointer',
																				}}>
																				<AddPhotoAlternateRoundedIcon sx={{ color: mainGreen }} />
																			</label>
																		</IconButton>
																		<input
																			multiple
																			accept='image/*'
																			onChange={onFileChange}
																			id='choose-photo-delete'
																			type='file'
																			style={{ display: 'none' }}
																		/>
																	</Box>
																</Box>
																<Box display='flex' justifyContent='space-between'>
																	<Button
																		disabled={disabled}
																		sx={{
																			mt: 2,
																			width: '75%',
																			mr: 1,
																			background: mainGreen,
																			'&:hover': { background: mainGreen },
																		}}
																		size='small'
																		variant='contained'
																		onClick={async () => {
																			await confirmReImportCookedStockSubmit(item._id, currentUser.id)
																		}}>
																		Xác nhận
																	</Button>
																	<Button
																		sx={{
																			mt: 2,
																			background: mainRed,
																			'&:hover': { background: mainRed },
																		}}
																		variant='contained'
																		size='small'
																		onClick={() => {
																			setShowDeleteOrderBackDrop(false)
																			setFileUpload([])
																		}}>
																		Hủy
																	</Button>
																</Box>
															</>
														)}
													</Card>
												</Backdrop>
												<Button
													onClick={() => {
														undoDeleteOrder(item._id)
													}}
													sx={{
														textTransform: 'none',
														fontSize: '10px',
														minWidth: 0,
														lineHeight: '0.7rem',
														background: mainRed,
														'&:hover': { background: mainRed },
													}}
													variant='contained'
													size='small'>
													Không chấp nhận
												</Button>
											</Box>
											<Divider sx={{ mt: 1, mb: 1 }} />
										</Fragment>
									)
								})}
							</Box>
						)}
					</Card>
				)}

				{currentUser.permission?.includes('confirmCheckStock') && (
					<>
						<Card
							sx={{
								mb: 1,
								padding: '10px',
							}}>
							<Typography
								sx={{
									textAlign: 'center',
									fontSize: '18px',
									fontWeight: 'bold',
									mb: 1,
								}}>
								KIỂM KHO SỐNG
							</Typography>
							{!confirmCheckFreshStock && (
								<Typography sx={{ textAlign: 'center', fontStyle: 'italic' }}>(Chưa có dữ liệu)</Typography>
							)}
							{confirmCheckFreshStock && (
								<>
									<Box>
										<Typography>
											Ca{' '}
											<b>
												{confirmCheckFreshStock?.shiftCode}-{confirmCheckFreshStock?.shiftDay}
											</b>
										</Typography>
										<Typography>
											<b>{confirmCheckFreshStock?.user.name}</b> kiểm lúc <b>{confirmCheckFreshStock?.timeStamp}</b>
										</Typography>
										<Typography>
											Tồn sống: <b>{confirmCheckFreshStock?.quantity?.toFixed(1)}</b> kg
										</Typography>
										<Typography>
											Tồn cuối ca hệ thống: <b>{confirmCheckFreshStock?.checkStockSystem?.toFixed(1)}</b> kg
										</Typography>
										<Typography>
											Chênh lệch:{' '}
											{confirmCheckFreshStock?.quantity - confirmCheckFreshStock?.checkStockSystem > 0 ? (
												<b>
													+
													{(confirmCheckFreshStock?.quantity - confirmCheckFreshStock?.checkStockSystem).toFixed(
														1,
													)}{' '}
												</b>
											) : (
												<b>
													{(confirmCheckFreshStock?.quantity - confirmCheckFreshStock?.checkStockSystem).toFixed(
														1,
													)}{' '}
												</b>
											)}
											kg
										</Typography>
										{confirmCheckFreshStock?.checkStockPicture && (
											<img
												loading='lazy'
												src={confirmCheckFreshStock?.checkStockPicture}
												onClick={(e) => {
													openImageViewer(confirmCheckFreshStock?.checkStockPicture)
												}}
												width='30'
												height='30'
												style={{
													marginRight: '8px',
													marginTop: '2px',
													cursor: 'pointer',
												}}
												alt=''
											/>
										)}
										{confirmCheckFreshStock?.checkStockMultiPicture?.map((img, index) => {
											return (
												<img
													loading='lazy'
													key={index}
													src={img}
													onClick={(e) => {
														openImageViewer(img)
													}}
													width='30'
													height='30'
													style={{
														marginRight: '8px',
														marginTop: '2px',
														cursor: 'pointer',
													}}
													alt=''
												/>
											)
										})}
									</Box>
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											mt: 1,
										}}>
										<Button
											disabled={disabled}
											onClick={async () => {
												await confirmCheckFreshStockSubmit(
													confirmCheckFreshStock?.quantity,
													confirmCheckFreshStock?._id,
												)
											}}
											sx={{
												textTransform: 'none',
												fontSize: '10px',
												minWidth: 0,
												lineHeight: '0.7rem',
												background: mainGreen,
												'&:hover': { background: mainGreen },
											}}
											variant='contained'
											size='small'>
											Xác nhận
										</Button>
										<Button
											onClick={() => {
												cancelCheckFreshStock()
											}}
											sx={{
												textTransform: 'none',
												fontSize: '10px',
												minWidth: 0,
												lineHeight: '0.7rem',
												background: mainRed,
												'&:hover': { background: mainRed },
											}}
											variant='contained'
											size='small'>
											Hủy
										</Button>
									</Box>
								</>
							)}
						</Card>
						<Card
							sx={{
								mb: 1,
								padding: '10px',
							}}>
							<Typography
								sx={{
									textAlign: 'center',
									fontSize: '18px',
									fontWeight: 'bold',
									mb: 1,
								}}>
								KIỂM KHO CHÍN
							</Typography>
							{!confirmCheckStock && (
								<Typography sx={{ textAlign: 'center', fontStyle: 'italic' }}>(Chưa có dữ liệu)</Typography>
							)}
							{confirmCheckStock && (
								<>
									<Box>
										<Typography>
											Ca{' '}
											<b>
												{confirmCheckStock?.shiftCode}-{confirmCheckStock?.shiftDay}
											</b>
										</Typography>
										<Typography>
											<b>{confirmCheckStock?.user.name}</b> kiểm lúc <b>{confirmCheckStock?.timeStamp}</b>
										</Typography>
										<Typography>
											Tồn chín: <b>{confirmCheckStock?.quantity?.toFixed(1)}</b> kg
										</Typography>
										<Typography>
											Tồn cuối ca hệ thống: <b>{confirmCheckStock?.checkStockSystem?.toFixed(1)}</b> kg
										</Typography>
										<Typography>
											Chênh lệch:{' '}
											{confirmCheckStock?.quantity - confirmCheckStock?.checkStockSystem > 0 ? (
												<b>+{(confirmCheckStock?.quantity - confirmCheckStock?.checkStockSystem).toFixed(1)} </b>
											) : (
												<b>{(confirmCheckStock?.quantity - confirmCheckStock?.checkStockSystem).toFixed(1)} </b>
											)}
											kg
										</Typography>
										{confirmCheckStock?.checkStockPicture && (
											<img
												loading='lazy'
												src={confirmCheckStock?.checkStockPicture}
												onClick={(e) => {
													openImageViewer(confirmCheckStock?.checkStockPicture)
												}}
												width='30'
												height='30'
												style={{
													marginRight: '8px',
													marginTop: '2px',
													cursor: 'pointer',
												}}
												alt=''
											/>
										)}

										{confirmCheckStock?.checkStockMultiPicture?.map((img, index) => {
											return (
												<img
													loading='lazy'
													key={index}
													src={img}
													onClick={(e) => {
														openImageViewer(img)
													}}
													width='30'
													height='30'
													style={{
														marginRight: '8px',
														marginTop: '2px',
														cursor: 'pointer',
													}}
													alt=''
												/>
											)
										})}
									</Box>
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											mt: 1,
										}}>
										<Button
											disabled={disabled}
											onClick={async () => {
												await confirmCheckProductStockSubmit(confirmCheckStock?.quantity, confirmCheckStock?._id)
											}}
											sx={{
												textTransform: 'none',
												fontSize: '10px',
												minWidth: 0,
												lineHeight: '0.7rem',
												background: mainGreen,
												'&:hover': { background: mainGreen },
											}}
											variant='contained'
											size='small'>
											Xác nhận
										</Button>
										<Button
											onClick={() => {
												cancelCheckProductStock()
											}}
											sx={{
												textTransform: 'none',
												fontSize: '10px',
												minWidth: 0,
												lineHeight: '0.7rem',
												background: mainRed,
												'&:hover': { background: mainRed },
											}}
											variant='contained'
											size='small'>
											Hủy
										</Button>
									</Box>
								</>
							)}
						</Card>
					</>
				)}

				{/* Duyệt đơn mua từ cửa hàng khác */}
				{true &&
					[
						{ type: 'SELL', title: 'ĐƠN MUA NỘI BỘ' },
						{ type: 'BUY', title: 'ĐƠN BÁN NỘI BỘ' },
					].map(({ type, title }) => (
						<Card sx={{ mb: 1, padding: '10px' }}>
							<Typography sx={{ textAlign: 'center', fontSize: '18px', fontWeight: 'bold', mb: 1 }}>{title}</Typography>
							{internalOrders.filter((item) => item.orderType === type)?.length === 0 && (
								<Typography sx={{ textAlign: 'center', fontStyle: 'italic' }}>(Chưa có dữ liệu)</Typography>
							)}
							{internalOrders
								.filter((item) => item.orderType === type)
								?.map((item, index) => {
									return (
										<Fragment key={index}>
											<Box>
												<Typography sx={{ textAlign: 'left', mt: 1 }}>
													<b>{item.sender.name}</b> (<b>{item.fromStore.name}</b>) tạo đơn: <b>{item.timeStamp}</b>
												</Typography>
												<Typography sx={{ textAlign: 'left', mt: 1 }}>
													● <b style={{ color: mainRed }}>{item.order?.freshQuantity} kg SỐNG</b> /{' '}
													<b style={{ color: mainGreen }}>{item.order?.cookedQuantity} kg CHÍN</b>
												</Typography>
												{item.order?.line_items
													?.filter((lineItem) => lineItem.quantity > 0)
													.map((lineItem) => (
														<Typography sx={{ textAlign: 'left' }}>
															● {lineItem.name}: <b>{lineItem.quantity} phần.</b>{' '}
														</Typography>
													))}
											</Box>
											{item?.order?.photoAttaches?.map((img, index) => {
												return (
													<img
														loading='lazy'
														key={`internal-sale-${type}-${index}`}
														src={img}
														onClick={(e) => openImageViewer(img)}
														width='30'
														height='30'
														style={{ marginRight: '8px', marginTop: '2px', cursor: 'pointer', objectFit: 'cover' }}
														alt=''
													/>
												)
											})}
											<Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
												<Button
													disabled={confirmCheckFreshStock || confirmCheckStock}
													onClick={async () => {
														actionInternalOrder(item._id, 'accept')
													}}
													sx={styles.accept}
													variant='contained'
													size='small'>
													Đồng ý
												</Button>
												<Button
													onClick={() => {
														actionInternalOrder(item._id, 'reject')
													}}
													sx={styles.reject}
													variant='contained'
													size='small'>
													Từ chối
												</Button>
											</Box>
											<Divider sx={{ mt: 1, mb: 1 }} />
										</Fragment>
									)
								})}
						</Card>
					))}
			</Box>
			<Backdrop
				sx={{
					color: '#fff',
					zIndex: 999,
					left: mediaMd ? 300 : 0,
				}}
				open={isViewerOpen}
				onClick={closeImageViewer}>
				<img
					loading='lazy'
					src={currentImage}
					alt='attach'
					style={{
						maxHeight: '70%',
						maxWidth: '90%',
					}}
					onClick={(e) => {
						e.stopPropagation()
					}}
				/>
			</Backdrop>
		</UserLayout>
	)
}

const styles = {
	accept: {
		textTransform: 'none',
		fontSize: '10px',
		minWidth: 0,
		lineHeight: '0.7rem',
		background: mainGreen,
		'&:hover': { background: mainGreen },
	},
	reject: {
		textTransform: 'none',
		fontSize: '10px',
		minWidth: 0,
		lineHeight: '0.7rem',
		background: mainRed,
		'&:hover': { background: mainRed },
	},
}
